<template>
  <div id="app" class="container-fluid">
    <!-- Header with Patient Info -->
    <full_head />

    <!-- Main Content Grid -->
    <div class="main-content">
      <!-- Loading State -->
      <div v-if="loading" class="text-center p-4">
        <i class="fas fa-spinner fa-spin"></i> Loading medical history...
      </div>

      <div v-else class="history-grid">
        <!-- Medical History Card -->
        <div class="card">
          <div class="card-header">
            <h3><i class="fas fa-clipboard-list"></i> Medical History</h3>
            <div class="header-actions">
              <button
                class="btn-add"
                @click="openConditionModal()"
                title="Add Medical Condition"
              >
                <i class="fas fa-plus"></i> Condition
              </button>
              <button
                class="btn-add"
                @click="openMedicationModal()"
                title="Add Medication"
              >
                <i class="fas fa-plus"></i> Medication
              </button>
              <button
                class="btn-add"
                @click="openAllergyModal()"
                title="Add Allergy"
              >
                <i class="fas fa-plus"></i> Allergy
              </button>
            </div>
          </div>
          <div class="card-body">
            <div class="medical-history-section">
              <h4><i class="fas fa-allergies"></i> Medical Conditions</h4>
              <div class="condition-list">
                <div
                  v-for="condition in medicalConditions"
                  :key="condition.id"
                  class="condition-item"
                >
                  <div class="condition-info">
                    <span class="condition-name">{{ condition.name }}</span>
                    <span class="condition-status" :class="condition.status">
                      {{ condition.status }}
                    </span>
                    <span class="condition-year" v-if="condition.diagnosed_year"
                      >Since {{ condition.diagnosed_year }}</span
                    >
                  </div>
                  <div class="condition-actions">
                    <button
                      class="btn-action edit"
                      @click="editCondition(condition)"
                      title="Edit"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button
                      class="btn-action delete"
                      @click="deleteRecord(condition.id, 'condition')"
                      title="Delete"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
                <p v-if="medicalConditions.length === 0" class="no-data">
                  No medical conditions recorded
                </p>
              </div>

              <h4><i class="fas fa-pills"></i> Current Medications</h4>
              <div v-if="medications.length > 0">
                <div
                  v-for="med in medications"
                  :key="med.id"
                  class="medication-item"
                >
                  <div class="medication-info">
                    <span class="med-name">{{ med.name }}</span>
                    <span class="med-dosage">{{ med.dosage }}</span>
                    <span class="med-purpose">{{ med.purpose }}</span>
                  </div>
                  <div class="condition-actions">
                    <button
                      class="btn-action edit"
                      @click="editMedication(med)"
                      title="Edit"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button
                      class="btn-action delete"
                      @click="deleteRecord(med.id, 'medication')"
                      title="Delete"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
              <p v-else class="no-data">No current medications</p>

              <h4><i class="fas fa-procedures"></i> Allergies</h4>
              <div class="allergies">
                <template v-if="allergies.length > 0">
                  <span
                    v-for="allergy in allergies"
                    :key="allergy.id"
                    class="allergy-tag"
                  >
                    {{ allergy.name }}
                    <i
                      class="fas fa-times"
                      @click="deleteRecord(allergy.id, 'allergy')"
                      style="cursor: pointer; margin-left: 5px; font-size: 10px"
                    ></i>
                  </span>
                </template>
                <span v-else class="no-data">No known allergies</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Family History Card -->
        <div class="card">
          <div class="card-header">
            <h3><i class="fas fa-family"></i> Family History</h3>
            <button
              class="btn-add"
              @click="openFamilyHistoryModal()"
              title="Add Family History"
            >
              <i class="fas fa-plus"></i> Add Record
            </button>
          </div>
          <div class="card-body">
            <div class="family-history">
              <div
                class="family-member"
                v-for="(history, member) in groupedFamilyHistory"
                :key="member"
              >
                <h5>{{ member }}</h5>
                <div class="family-conditions">
                  <template v-if="history.length > 0">
                    <span
                      v-for="fh in history"
                      :key="fh.id"
                      class="family-condition-tag"
                    >
                      {{ fh.condition }}
                      <i
                        class="fas fa-times"
                        @click="deleteRecord(fh.id, 'family_history')"
                        style="
                          cursor: pointer;
                          margin-left: 5px;
                          font-size: 10px;
                        "
                      ></i>
                    </span>
                  </template>
                  <span v-else class="no-data">No significant history</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Medical Condition Modal -->
    <div v-if="showConditionModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingCondition ? "Edit" : "Add" }} Medical Condition</h3>
          <button class="modal-close" @click="closeConditionModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveCondition">
            <div class="form-group">
              <label>Condition Name *</label>
              <input
                type="text"
                v-model="conditionForm.name"
                required
                class="form-control"
                placeholder="e.g., Hypertension"
              />
            </div>
            <div class="form-group">
              <label>Status *</label>
              <select
                v-model="conditionForm.status"
                required
                class="form-control"
              >
                <option value="">Select Status</option>
                <option value="active">Active</option>
                <option value="controlled">Controlled</option>
                <option value="intermittent">Intermittent</option>
                <option value="resolved">Resolved</option>
                <option value="history_of">History Of</option>
              </select>
            </div>
            <div class="form-group">
              <label>Diagnosed Year</label>
              <input
                type="number"
                v-model="conditionForm.diagnosed_year"
                class="form-control"
                placeholder="e.g., 2020"
                min="1900"
                :max="new Date().getFullYear()"
              />
            </div>
            <div class="form-group">
              <label>Severity</label>
              <select v-model="conditionForm.severity" class="form-control">
                <option value="">Select Severity</option>
                <option value="mild">Mild</option>
                <option value="moderate">Moderate</option>
                <option value="severe">Severe</option>
              </select>
            </div>
            <div class="form-group">
              <label>Remarks</label>
              <textarea
                v-model="conditionForm.remarks"
                class="form-control"
                placeholder="Optional notes"
                rows="2"
              ></textarea>
            </div>
            <div class="modal-actions">
              <button
                type="button"
                class="btn btn-secondary"
                @click="closeConditionModal"
              >
                Cancel
              </button>
              <button type="submit" class="btn btn-primary" :disabled="saving">
                {{
                  saving ? "Saving..." : editingCondition ? "Update" : "Save"
                }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Medication Modal -->
    <div v-if="showMedicationModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingMedication ? "Edit" : "Add" }} Medication</h3>
          <button class="modal-close" @click="closeMedicationModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveMedication">
            <div class="form-group">
              <label>Medication Name *</label>
              <input
                type="text"
                v-model="medicationForm.name"
                required
                class="form-control"
                placeholder="e.g., Folic Acid"
              />
            </div>
            <div class="form-group">
              <label>Dosage *</label>
              <input
                type="text"
                v-model="medicationForm.dosage"
                required
                class="form-control"
                placeholder="e.g., 400mg daily"
              />
            </div>
            <div class="form-group">
              <label>Purpose</label>
              <input
                type="text"
                v-model="medicationForm.purpose"
                class="form-control"
                placeholder="e.g., Pregnancy supplement"
              />
            </div>
            <div class="form-group">
              <label>Frequency</label>
              <input
                type="text"
                v-model="medicationForm.frequency"
                class="form-control"
                placeholder="e.g., Once daily"
              />
            </div>
            <div class="form-group">
              <label>Remarks</label>
              <textarea
                v-model="medicationForm.remarks"
                class="form-control"
                placeholder="Optional notes"
                rows="2"
              ></textarea>
            </div>
            <div class="modal-actions">
              <button
                type="button"
                class="btn btn-secondary"
                @click="closeMedicationModal"
              >
                Cancel
              </button>
              <button type="submit" class="btn btn-primary" :disabled="saving">
                {{
                  saving ? "Saving..." : editingMedication ? "Update" : "Save"
                }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Allergy Modal -->
    <div v-if="showAllergyModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingAllergy ? "Edit" : "Add" }} Allergy</h3>
          <button class="modal-close" @click="closeAllergyModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveAllergy">
            <div class="form-group">
              <label>Allergy Name *</label>
              <input
                type="text"
                v-model="allergyForm.name"
                required
                class="form-control"
                placeholder="e.g., Penicillin"
              />
            </div>
            <div class="form-group">
              <label>Type</label>
              <select v-model="allergyForm.type" class="form-control">
                <option value="">Select Type</option>
                <option value="Drug">Drug</option>
                <option value="Food">Food</option>
                <option value="Environmental">Environmental</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div class="form-group">
              <label>Severity</label>
              <select v-model="allergyForm.severity" class="form-control">
                <option value="">Select Severity</option>
                <option value="mild">Mild</option>
                <option value="moderate">Moderate</option>
                <option value="severe">Severe</option>
                <option value="life_threatening">Life Threatening</option>
              </select>
            </div>
            <div class="form-group">
              <label>Reaction</label>
              <input
                type="text"
                v-model="allergyForm.reaction"
                class="form-control"
                placeholder="e.g., Rash, Swelling"
              />
            </div>
            <div class="modal-actions">
              <button
                type="button"
                class="btn btn-secondary"
                @click="closeAllergyModal"
              >
                Cancel
              </button>
              <button type="submit" class="btn btn-primary" :disabled="saving">
                {{ saving ? "Saving..." : editingAllergy ? "Update" : "Save" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Family History Modal -->
    <div v-if="showFamilyHistoryModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingFamilyHistory ? "Edit" : "Add" }} Family History</h3>
          <button class="modal-close" @click="closeFamilyHistoryModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveFamilyHistory">
            <div class="form-group">
              <label>Family Member *</label>
              <select
                v-model="familyHistoryForm.relationship"
                required
                class="form-control"
              >
                <option value="">Select Family Member</option>
                <option value="Mother">Mother</option>
                <option value="Father">Father</option>
                <option value="Siblings">Siblings</option>
                <option value="Maternal Grandmother">
                  Maternal Grandmother
                </option>
                <option value="Maternal Grandfather">
                  Maternal Grandfather
                </option>
                <option value="Paternal Grandmother">
                  Paternal Grandmother
                </option>
                <option value="Paternal Grandfather">
                  Paternal Grandfather
                </option>
              </select>
            </div>
            <div class="form-group">
              <label>Medical Condition *</label>
              <input
                type="text"
                v-model="familyHistoryForm.condition"
                required
                class="form-control"
                placeholder="e.g., Type 2 Diabetes"
              />
            </div>
            <div class="form-group">
              <label>Age at Diagnosis</label>
              <input
                type="number"
                v-model="familyHistoryForm.age_at_diagnosis"
                class="form-control"
                placeholder="e.g., 45"
                min="0"
                max="120"
              />
            </div>
            <div class="form-group">
              <label>Status</label>
              <select v-model="familyHistoryForm.status" class="form-control">
                <option value="alive">Alive</option>
                <option value="deceased">Deceased</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>
            <div class="modal-actions">
              <button
                type="button"
                class="btn btn-secondary"
                @click="closeFamilyHistoryModal"
              >
                Cancel
              </button>
              <button type="submit" class="btn btn-primary" :disabled="saving">
                {{
                  saving
                    ? "Saving..."
                    : editingFamilyHistory
                    ? "Update"
                    : "Save"
                }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import full_head from "./full_head.vue";
import { generalApiRequest } from "../../../../api/apiIndex";
import showNotification from "../../../../notification/notify";

export default {
  name: "MaternalProfile",
  components: {
    full_head,
  },
  data() {
    return {
      loading: false,
      saving: false,
      patientId: null,
      visitId: null,
      ancSessionId: null,

      // Data from API
      medicalConditions: [],
      medications: [],
      allergies: [],
      familyHistories: [],

      // Modal controls
      showConditionModal: false,
      showMedicationModal: false,
      showAllergyModal: false,
      showFamilyHistoryModal: false,

      // Editing controls
      editingCondition: null,
      editingMedication: null,
      editingAllergy: null,
      editingFamilyHistory: null,

      // Form data
      conditionForm: {
        name: "",
        status: "",
        diagnosed_year: null,
        severity: "",
        remarks: "",
      },
      medicationForm: {
        name: "",
        dosage: "",
        purpose: "",
        frequency: "",
        remarks: "",
      },
      allergyForm: {
        name: "",
        type: "",
        severity: "",
        reaction: "",
      },
      familyHistoryForm: {
        relationship: "",
        condition: "",
        age_at_diagnosis: null,
        status: "alive",
      },
    };
  },
  computed: {
    groupedFamilyHistory() {
      const grouped = {};
      this.familyHistories.forEach((fh) => {
        if (!grouped[fh.relationship]) {
          grouped[fh.relationship] = [];
        }
        grouped[fh.relationship].push(fh);
      });
      return grouped;
    },
  },
  created() {
    this.initializeIds();
    this.fetchMedicalHistory();
  },
  methods: {
    initializeIds() {
      this.patientId = this.$route.params.patient_id || null;
      this.visitId = this.$route.params.visit_id || null;
      this.ancSessionId = this.$route.params.anc_session_id || null;
    },

    async fetchMedicalHistory() {
      this.loading = true;
      try {
        const response = await generalApiRequest(
          "get-medical-history",
          "GET",
          {
            patient_id: this.patientId,
            visit_id: this.visitId,
            anc_session_id: this.ancSessionId,
          },
          1,
        );

        if (response.success) {
          const data = response.data;
          this.medicalConditions = data.conditions || [];
          this.medications = data.medications || [];
          this.allergies = data.allergies || [];
          this.familyHistories = data.family_history || [];
        }
      } catch (error) {
        console.error("Error fetching medical history:", error);
        showNotification("Failed to load medical history", "error");
      } finally {
        this.loading = false;
      }
    },

    // Generic save method
    async saveRecord(endpoint, form, editing, closeModalFn) {
      this.saving = true;
      try {
        const req_body = {
          patient_id: this.patientId,
          visit_id: this.visitId,
          anc_session_id: this.ancSessionId,
          ...form,
        };

        if (editing) {
          req_body.record_id = editing.id;
        }

        const response = await generalApiRequest(endpoint, "POST", req_body, 1);

        if (response.success) {
          showNotification(
            editing
              ? "Record updated successfully"
              : "Record created successfully",
            "success",
          );
          await this.fetchMedicalHistory();
          closeModalFn();
        } else {
          throw new Error(response.message || "Operation failed");
        }
      } catch (error) {
        console.error("Error saving record:", error);
        showNotification(error.message || "Failed to save record", "error");
      } finally {
        this.saving = false;
      }
    },

    async deleteRecord(recordId, type) {
      if (!confirm("Are you sure you want to delete this record?")) {
        return;
      }

      try {
        const response = await generalApiRequest(
          "delete-medical-history-record",
          "POST",
          { record_id: recordId, type: type },
          1,
        );

        if (response.success) {
          showNotification("Record deleted successfully", "success");
          await this.fetchMedicalHistory();
        } else {
          throw new Error(response.message || "Delete failed");
        }
      } catch (error) {
        console.error("Error deleting record:", error);
        showNotification("Failed to delete record", "error");
      }
    },

    // Condition methods
    openConditionModal(condition = null) {
      this.editingCondition = condition;
      if (condition) {
        this.conditionForm = {
          name: condition.name,
          status: condition.status,
          diagnosed_year: condition.diagnosed_year,
          severity: condition.severity || "",
          remarks: condition.remarks || "",
        };
      } else {
        this.resetConditionForm();
      }
      this.showConditionModal = true;
    },

    closeConditionModal() {
      this.showConditionModal = false;
      this.editingCondition = null;
      this.resetConditionForm();
    },

    resetConditionForm() {
      this.conditionForm = {
        name: "",
        status: "",
        diagnosed_year: null,
        severity: "",
        remarks: "",
      };
    },

    editCondition(condition) {
      this.openConditionModal(condition);
    },

    saveCondition() {
      const endpoint = this.editingCondition
        ? "update-medical-condition"
        : "create-medical-condition";
      this.saveRecord(
        endpoint,
        this.conditionForm,
        this.editingCondition,
        this.closeConditionModal,
      );
    },

    // Medication methods
    openMedicationModal(medication = null) {
      this.editingMedication = medication;
      if (medication) {
        this.medicationForm = {
          name: medication.name,
          dosage: medication.dosage,
          purpose: medication.purpose || "",
          frequency: medication.frequency || "",
          remarks: medication.remarks || "",
        };
      } else {
        this.resetMedicationForm();
      }
      this.showMedicationModal = true;
    },

    closeMedicationModal() {
      this.showMedicationModal = false;
      this.editingMedication = null;
      this.resetMedicationForm();
    },

    resetMedicationForm() {
      this.medicationForm = {
        name: "",
        dosage: "",
        purpose: "",
        frequency: "",
        remarks: "",
      };
    },

    editMedication(medication) {
      this.openMedicationModal(medication);
    },

    saveMedication() {
      const endpoint = this.editingMedication
        ? "update-current-medication"
        : "create-current-medication";
      this.saveRecord(
        endpoint,
        this.medicationForm,
        this.editingMedication,
        this.closeMedicationModal,
      );
    },

    // Allergy methods
    openAllergyModal(allergy = null) {
      this.editingAllergy = allergy;
      if (allergy) {
        this.allergyForm = {
          name: allergy.name,
          type: allergy.type || "",
          severity: allergy.severity || "",
          reaction: allergy.reaction || "",
        };
      } else {
        this.resetAllergyForm();
      }
      this.showAllergyModal = true;
    },

    closeAllergyModal() {
      this.showAllergyModal = false;
      this.editingAllergy = null;
      this.resetAllergyForm();
    },

    resetAllergyForm() {
      this.allergyForm = {
        name: "",
        type: "",
        severity: "",
        reaction: "",
      };
    },

    saveAllergy() {
      const endpoint = this.editingAllergy
        ? "update-allergy"
        : "create-allergy";
      this.saveRecord(
        endpoint,
        this.allergyForm,
        this.editingAllergy,
        this.closeAllergyModal,
      );
    },

    // Family History methods
    openFamilyHistoryModal(familyHistory = null) {
      this.editingFamilyHistory = familyHistory;
      if (familyHistory) {
        this.familyHistoryForm = {
          relationship: familyHistory.relationship,
          condition: familyHistory.condition,
          age_at_diagnosis: familyHistory.age_at_diagnosis,
          status: familyHistory.status || "alive",
        };
      } else {
        this.resetFamilyHistoryForm();
      }
      this.showFamilyHistoryModal = true;
    },

    closeFamilyHistoryModal() {
      this.showFamilyHistoryModal = false;
      this.editingFamilyHistory = null;
      this.resetFamilyHistoryForm();
    },

    resetFamilyHistoryForm() {
      this.familyHistoryForm = {
        relationship: "",
        condition: "",
        age_at_diagnosis: null,
        status: "alive",
      };
    },

    saveFamilyHistory() {
      const endpoint = this.editingFamilyHistory
        ? "update-family-history"
        : "create-family-history";
      this.saveRecord(
        endpoint,
        this.familyHistoryForm,
        this.editingFamilyHistory,
        this.closeFamilyHistoryModal,
      );
    },
  },
};
</script>

<style scoped>
.container-fluid {
  padding: 20px;
  background-color: #f8f9fa;
}

.main-content {
  margin-top: 20px;
}

.history-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 20px;
}

.card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  border: none;
  overflow: hidden;
  height: 100%;
}

.card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 15px;
  border-bottom: 2px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.card-header h3 {
  margin: 0;
  color: #495057;
  font-size: 18px;
  font-weight: 600;
}

.card-header h3 i {
  margin-right: 10px;
  color: #667eea;
}

.header-actions {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.btn-add {
  padding: 5px 12px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

.btn-add:hover {
  background: #218838;
}

.btn-add i {
  font-size: 10px;
}

.card-body {
  padding: 20px;
}

.medical-history-section h4 {
  color: #495057;
  margin: 20px 0 12px;
  font-size: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
}

.medical-history-section h4:first-child {
  margin-top: 0;
}

.medical-history-section h4 i {
  margin-right: 8px;
  color: #28a745;
}

.condition-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}

.condition-item {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 8px;
  border-left: 4px solid #667eea;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
}

.condition-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1;
}

.condition-actions {
  display: flex;
  gap: 5px;
}

.condition-name {
  font-weight: 600;
  color: #212529;
  min-width: 150px;
}

.condition-status {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 15px;
  font-weight: 600;
}

.condition-status.controlled {
  background: #d4edda;
  color: #155724;
}

.condition-status.resolved {
  background: #cce5ff;
  color: #004085;
}

.condition-status.intermittent {
  background: #fff3cd;
  color: #856404;
}

.condition-status.active {
  background: #f8d7da;
  color: #721c24;
}

.condition-status.history_of {
  background: #e2e3e5;
  color: #383d41;
}

.condition-year {
  font-size: 13px;
  color: #6c757d;
}

.medication-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  padding: 12px;
  border-radius: 8px;
  border-left: 4px solid #28a745;
  margin-bottom: 10px;
  flex-wrap: wrap;
  gap: 8px;
}

.medication-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1;
}

.med-name {
  font-weight: 600;
  color: #212529;
  min-width: 120px;
}

.med-dosage {
  color: #dc3545;
  font-weight: 600;
  font-size: 14px;
}

.med-purpose {
  color: #6c757d;
  font-size: 13px;
}

.allergies {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.allergy-tag {
  background: #f8d7da;
  color: #721c24;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
}

.btn-action {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.btn-action.edit {
  background: #3498db;
  color: white;
}

.btn-action.edit:hover {
  background: #2980b9;
}

.btn-action.delete {
  background: #e74c3c;
  color: white;
}

.btn-action.delete:hover {
  background: #c0392b;
}

.family-history {
  padding: 15px;
}

.family-member {
  margin-bottom: 20px;
}

.family-member h5 {
  color: #495057;
  margin-bottom: 8px;
  font-size: 15px;
  font-weight: 600;
  padding-bottom: 5px;
  border-bottom: 1px solid #e9ecef;
}

.family-conditions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.family-condition-tag {
  background: #e9ecef;
  color: #495057;
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.no-data {
  text-align: center;
  padding: 20px;
  color: #6c757d;
  font-style: italic;
  background: #f8f9fa;
  border-radius: 8px;
  margin: 15px 0;
  font-size: 14px;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 10px;
  width: 90%;
  max-width: 450px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  padding: 15px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
}

.modal-close {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  color: #495057;
  font-size: 14px;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.btn {
  padding: 8px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a6fd8;
}

textarea.form-control {
  resize: vertical;
  min-height: 60px;
}

/* Small screen optimizations */
@media (max-width: 576px) {
  .container-fluid {
    padding: 10px;
  }

  .history-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .card-body {
    padding: 15px;
  }

  .condition-item,
  .medication-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .condition-info,
  .medication-info {
    width: 100%;
  }

  .condition-actions {
    align-self: flex-end;
  }

  .condition-name,
  .med-name {
    min-width: 100%;
  }

  .card-header h3 {
    font-size: 16px;
  }

  .medical-history-section h4 {
    font-size: 15px;
  }

  .header-actions {
    width: 100%;
  }

  .btn-add {
    flex: 1;
    justify-content: center;
  }
}

/* Very small screens */
@media (max-width: 380px) {
  .history-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .family-condition-tag,
  .allergy-tag {
    font-size: 12px;
    padding: 4px 10px;
  }

  .card-header {
    padding: 12px;
  }

  .card-body {
    padding: 12px;
  }
}
</style>
